import {
  Box,
  Card,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import produce from 'immer';
import _, { isArray } from 'lodash';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { CareTeamWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CareTeam';
import {
  ConsentWrapper,
  WrappedConsent,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Consent';
import { DocumentReferenceWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/DocumentReference';
import {
  PatientWrapper,
  WrappedPatient,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { PractitionerRoleWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PractitionerRole';
import { RelatedPersonWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/RelatedPerson';
import { ServiceRequestWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import { TaskWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import {
  useCareTeams,
  useConsents,
  useDocumentReferences,
  usePatient,
  usePractitionerRoles,
  useRelatedPersons,
  useServiceRequests,
  useTasks,
} from 'src/careflow/ph-core/fhir-client/fhir-react';
import { CareTeamMemberWithEditableData } from 'src/@types/crs/case';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import DialogCloseButton from 'src/components/DialogCloseButton';
import Page from 'src/components/Page';
import { TableCustom } from 'src/components/TableCustom';
import ExpandableCard from 'src/components/common/ExpandableCard';
import ComplementaryInfo from 'src/components/complementary-info/ComplementaryInfo';
import SelectType from 'src/careflow/ph-core/workflow/components/common/basic-step/Select';
import {
  PatientEngagementResponse,
  PatientEngagementService,
} from 'src/careflow/engagement-hub/patient-engagement/service/PatientService';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import useSettings from 'src/careflow/ph-core/multi-tenant/setting/useSettings';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { PATH_DASHBOARD } from 'src/routes/paths';
import TasksByPathway from 'src/careflow/care-coordination/task/patient/TasksByPathway';
import { commonMenuTaskHandlers } from 'src/careflow/care-coordination/task/common/handlers';
import CustomLink from 'src/careflow/common/CustomLink';
import { filterTasksByDefinition } from 'src/careflow/helpers/common';
import GeneralPractitionerList from 'src/careflow/care-coordination/patient/general-practitioner/GeneralPractitionerList';
import { RelatedPersonList } from 'src/careflow/care-coordination/patient/related-person/RelatedPersonList';
import { DocumentList } from 'src/careflow/care-coordination/patient/attachments/DocumentList';
import PatientCase from 'src/careflow/care-coordination/patient/cases/patientCase';
import {
  PatientEngagementPermissions,
  ResourceWithIncludedResources,
  TaskPermissions,
} from 'src/careflow/helpers/types';
import ConsentInformation, {
  getSelectOptionCustomButton as getConsentOption,
} from 'src/careflow/engagement-hub/patient-engagement/patient/components/workflow-step/ConsentInformation';
import { getRegistries } from 'src/careflow/ph-core/multi-tenant/registry/registry';
import { formattedDateTimeZone } from 'src/utils/dates';
import { getReferenceId, getValuesFromReference, setSILDisplay } from 'src/utils/fhir';
import { dateYearFormatWithAge } from 'src/utils/formatTime';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import {
  ACLValidationMode,
  checkAclValidation,
  getRelatedAcls,
} from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import { getParseColumn } from 'src/careflow/ph-core/workflow/helpers/util';
import KeyValueGrid from '../../../../components/KeyValueGrid';
import NotesList from '../common/notes/NotesLists';
import { TABLE_HEAD_CONTACT } from './components/table-head';
import AssessmentFromChecklistData from './components/workflow-step/AssessmentFromChecklistData';
import ProviderInformation, {
  getSelectOptionCustomButton,
  PayloadProvider,
  workflowInstanceDataProvider,
} from './components/workflow-step/ProviderInformation';
import ReferralInformation, {
  getDisplay,
  getReferred,
} from '../../../care-coordination/referral/components/workflow-step/ReferralInformation';
import ConfirmCMOptInItem from './components/workflow-step/ConfirmCMOptInItem';
import ConfirmContact from './components/workflow-step/ConfirmContact';
import ConfirmNatOptIn from './components/workflow-step/ConfirmNatOptIn';
import TasksList from './components/workflow-step/ListTasks';
import ReviewNatReportItem from './components/workflow-step/ReviewNatReportItem';
import ScheduleAppointment from './components/workflow-step/ScheduleAppointment';
import {
  getConsents,
  handlerGenericStepWorkflow,
  mapActivityDefinitionTask,
  mapFhirResourceReference,
  workflowInstanceDataStep,
} from 'src/careflow/ph-core/workflow/helpers/workflow';
import PatientEngagementInformation from './components/PatientEngagementInformation';
import { AppointmentList } from 'src/careflow/care-coordination/patient/appointments/AppointmentList';
import { createTask } from 'src/careflow/care-coordination/task/main/task.api';
import useHealthCareServicesByServiceRequests from 'src/hooks/useHealthCareServicesByServiceRequests';
import useCheckNTaskLinker from 'src/hooks/useCheckNTaskLinker';
import { fhirClient } from 'src/App';
import { PractitionerRole } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/PractitionerRole';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import AddRequest from 'src/careflow/inck/nat-request/AddRequest';
import { WorkflowHeader } from 'src/careflow/ph-core/workflow/common/WorkflowHeader';
import Workflow, { WorkflowData } from 'src/careflow/ph-core/workflow/Workflow';
import { WorkflowStatistics } from 'src/careflow/ph-core/workflow/common/WorkflowStatistics';
import { CREATE_TASK, UPDATE_TASK, useEventBus } from 'src/event-bus';
import AssessmentFrame from 'src/careflow/care-coordination/patient/assessment/frame/AssessmentFrame';
import useTranslations from 'src/careflow/ph-core/internationalization/hook/useTranslations';
import { ReferralService } from 'src/crs/referral/services/ReferralService';
import { WorkflowService } from 'src/careflow/ph-core/workflow/api/WorkflowService';
import { registerSteps } from 'src/careflow/ph-core/workflow/registry/workflowRegistry';
import PatientTagSelector from './components/workflow-step/PatientTagSelector';
import { UserService } from 'src/careflow/ph-core/admin/user/UserService';
import { mapContactAttempts } from '../common/common-map';
import ConfirmMemberInfo from './components/workflow-step/ConfirmMemberInfo';
import ConfirmGuarantorInfo from './components/workflow-step/ConfirmGuarantorInfo';
import { assignOwnerHandler } from 'src/careflow/ph-core/workflow/handlers';
import { useTaskActivityDefinition, useTaskAssessment } from 'src/careflow/helpers/hooks';

export default function PatientEngagementDetails() {
  const { id: patientEngagementId = null } = useParams();
  const user = useAuth();
  const userEmail = user?.getCurrentUser?.()?.email;
  const { themeStretch } = useSettings();
  const translate = useTranslations(`assessmentDialog`);
  const { enqueueSnackbar } = useSnackbar();
  const { i18n } = useLocales();
  const { componentsData, configurations } = useTenantConfigData();
  const { showOptInStatus } = componentsData ?? {};
  const { onEvent, removeEvent } = useEventBus();

  const [patientId, setPatientId] = useState('');
  const [patientEngagement, setPatientEngagement] = useState<
    PatientEngagementResponse | undefined
  >();
  const [contactAttempts, setContactAttempts] = useState<any>([]);
  const [allUsers, setAllUsers] = useState([]);

  const [workflowStage, setWorkflowStage] = useState(undefined);
  const [workflowInstance, setWorkflowInstance] = useState<any>();
  const [metadataWorkflow, setMetadataWorkflow] = useState<any>({});
  const [checklistData, setChecklistData] = useState<any>([]);
  const [reject, setReject] = useState(false);
  const [disposition, setDisposition] = useState<any>(false);
  const [rejectExternal, setRejectExternal] = useState(false);
  const [intakeOption, setIntakeOption] = useState<{ providers?: any[]; consents?: any[] }>({
    providers: [],
    consents: [],
  });
  const [consents, setConsents] = useState<ResourceWithIncludedResources<WrappedConsent>[]>([]);
  const [refreshConsents, setRefreshConsents] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [statisticsData, setStatisticsData] = useState<any>(null);
  const [checklistRefreshKey, setChecklistRefreshKey] = useState(0);

  const [modals, setModals] = useState({
    openDialog: false,
    confirmOptInItem: false,
    reviewNatReportItem: false,
    showTasksList: false,
    confirmNatOptIn: false,
    scheduleAppointment: false,
    confirmContact: false,
    addRequest: false,
    confirmMemberInfo: false,
    confirmGuarantorInfo: false,
    referralInfo: false,
    consents: false,
    providerInformation: false,
    screeningInterview: false,
    chartReview: false,
    confirmScreeningResponses: false,
    reAssign: false,
  });

  const toggleModal = useCallback((key: keyof typeof modals, value: boolean) => {
    setModals((prev) => ({ ...prev, [key]: value }));
  }, []);

  const [referredSR, setReferredSR] = useState('');

  const [relatedPersonsOpen, setRelatedPersonsOpen] = useState(false);

  useEffect(() => {
    if (patientEngagement) {
      const patientIdFhir = patientEngagement.patientFhirUUID;
      if (patientIdFhir) {
        setPatientId(patientIdFhir);
      }
    }
  }, [patientEngagement]);

  const [
    patient,
    { isFetching: isPatientFetching, refresh: refreshPatient, update: updatePatient },
  ] = usePatient(patientId, {
    map: PatientWrapper,
    autofetch: !!patientId,
  });

  const [natTasks, { refresh: refreshTask }] = useTasks({
    filter: {
      patient: patient?.id,
      code: 'completed-nat-assessment',
    },
    autofetch: !!patient?.id,
    map: TaskWrapper,
  });

  const [consent] = useConsents({
    filter: {
      patient: patient?.id,
    },
    autofetch: !!patient?.id,
    map: ConsentWrapper,
  });

  const [
    relatedPersons,
    { create: createRelatedPerson, update: updateRelatedPerson, refresh: refreshRelatedPerson },
  ] = useRelatedPersons({
    filter: {
      patient: patient?.id,
      active: true,
    },
    autofetch: !!patient,
    map: RelatedPersonWrapper,
  });

  const [serviceRequest, { refresh: refreshServiceRequest }] = useServiceRequests({
    filter: {
      category: 'intake-referral',
      code: 'enroll-to-sbha-program',
      subject: patient?.id,
    },
    map: ServiceRequestWrapper,
    autofetch: !!patientId,
  });

  const [
    careTeams,
    { isFetching: isCareTeamFetching, update: updateCareTeams, refresh: refreshCareTeams },
  ] = useCareTeams({
    filter: {
      subject: patient?.id,
    },
    map: CareTeamWrapper,
    autofetch: !!patient,
  });

  const practitionerRolesId = useMemo(
    () =>
      patient?.generalPractitioner
        ?.filter(({ reference }) => reference?.includes('PractitionerRole'))
        .map(({ reference }) => {
          const [_, id] = getValuesFromReference(reference);
          return id;
        }),
    [patient]
  );

  const [
    practitionerRoles,
    { isFetching: isPractitionerRoleFetching, refresh: refreshPractitionerRoles },
  ] = usePractitionerRoles({
    filter: {
      _id: practitionerRolesId?.join(','),
      active: true,
    },
    map: PractitionerRoleWrapper,
    autofetch: !!practitionerRolesId?.length,
  });

  const filteredPractitionerRoles = useMemo(() => {
    if (
      practitionerRoles &&
      isArray(practitionerRoles) &&
      practitionerRoles.length > 0 &&
      practitionerRolesId &&
      isArray(practitionerRolesId) &&
      practitionerRolesId.length > 0 &&
      patient?.generalPractitioner &&
      isArray(patient?.generalPractitioner) &&
      patient?.generalPractitioner.length > 0
    ) {
      const filtered = practitionerRoles.filter((x) => x?.id && practitionerRolesId.includes(x.id));
      return filtered;
    }
    return [];
  }, [practitionerRoles, practitionerRolesId, patient]);

  const [documentReferences, { refresh: refreshDocumentReference }] = useDocumentReferences({
    filter: { subject: patient?.id },
    map: DocumentReferenceWrapper,
    autofetch: !!patient,
  });

  const getPatientEngagementDetails = async (id: any) => {
    try {
      const resp = await PatientEngagementService.getOne(id);
      setPatientEngagement(resp);
    } catch (e) {
      console.log(`Something failed get patient engagement details ${e}`);
    }
  };

  const fetchStatistics = async (patientEngagementId: string | undefined) => {
    if (!patientEngagementId) return;
    const result = await PatientEngagementService.getStatistics(patientEngagementId);
    setStatisticsData(result);
  };

  useEffect(() => {
    getPatientEngagementDetails(patientEngagementId);

    return () => {
      setPatientEngagement(undefined);
    };
  }, [patientEngagementId]);

  const fetchStageFromWorkflow = async () => {
    if (!patientEngagement) return;
    try {
      const result = await WorkflowService.getInstance(
        patientEngagement?.patientEngagementId,
        patientEngagement?.workflowReferenceName
      );
      setWorkflowInstance(result);
      setWorkflowStage(result?.stage?.id);
      const components = result?.workFlow?.metadata
        ? JSON.parse(result?.workFlow?.metadata).components?.workflow
        : null;
      setMetadataWorkflow(components);
      fetchStatistics(patientEngagement?.patientEngagementId);
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchContactAttempt = async (patientEngagementId: string) => {
    const result: any = await PatientEngagementService.getContactAttempt(patientEngagementId);
    setContactAttempts(result);
  };

  const fetchAllUsers = async (_workflowInstance, _patient) => {
    if (!_workflowInstance?.scope) {
      return;
    }

    try {
      // Extract ACL requirements from workflow metadata
      const workflowMetadata = _workflowInstance?.workFlow?.metadata
        ? JSON.parse(_workflowInstance.workFlow.metadata)
        : {};
      const ownerACLs = workflowMetadata?.workflow?.ownerAllowedACLs;

      let result;
      if (ownerACLs && ownerACLs.length > 0) {
        // Use ACL-based filtering when configuration exists
        const relatedACLs = getRelatedAcls(ownerACLs);
        result = await UserService.getAllUsersByAcls(relatedACLs);
      } else {
        // Fallback to existing behavior when no ACL configuration
        const managingOrgId = _patient?.managingOrganization?.reference?.split('/')[1];
        result = await UserService.getUsersByOrgAndRole(managingOrgId ? [managingOrgId] : [], []);
      }

      setAllUsers(
        result
          .sort((_a: any, _b: any) => {
            const a = (_a.firstName + ' ' + _a.lastName).toLowerCase(),
              b = (_b.firstName + ' ' + _b.lastName).toLowerCase();

            if (a < b) return -1;

            if (a > b) return 1;

            return 0;
          })
          .filter((e: any) => e.status === 'Active')
      );
    } catch (err) {
      console.log('error on fetching all users', err);
    }
  };

  useEffect(() => {
    fetchStageFromWorkflow();

    if (!patientEngagement?.patientEngagementId) {
      return;
    }

    fetchContactAttempt(patientEngagement?.patientEngagementId);
  }, [patientEngagement]);

  useEffect(() => {
    if (workflowInstance?.scope) {
      fetchAllUsers(workflowInstance, patient);
    }
  }, [workflowInstance?.scope, patient]);

  useEffect(() => {
    const getReferredFromServiceRequest = async () => {
      const sr = serviceRequest[0];
      const isExternal = (sr?.category as any[])?.some(
        (c: any) =>
          c?.coding?.[0]?.system ===
            'https://mycarecircle.site/fhir/CodeSystem/ph-referral-source-type' &&
          c?.coding?.[0]?.code === 'external'
      );
      const externalOrgTypeExt = isExternal
        ? sr?.extension?.find(
            (e: any) =>
              e.url === 'https://mycarecircle.site/fhir/extension/external-referral-org-type'
          )
        : null;
      if (externalOrgTypeExt) {
        const orgTypeDisplay =
          externalOrgTypeExt.valueCodeableConcept?.text ??
          externalOrgTypeExt.valueCodeableConcept?.coding?.[0]?.display ??
          '';
        const externalOrgNameExt = sr?.extension?.find(
          (e: any) =>
            e.url === 'https://mycarecircle.site/fhir/extension/external-referral-org-name'
        );
        const orgName = externalOrgNameExt?.valueString;
        setReferredSR(orgName ? `${orgTypeDisplay} — ${orgName}` : orgTypeDisplay);
        return;
      }
      const { referred } = await getReferred(sr);
      const stringReferred = referred ? getDisplay(referred) : '';
      setReferredSR(stringReferred);
    };

    getReferredFromServiceRequest();
  }, [serviceRequest]);

  const referralInfoItemData = useMemo(
    () => [
      {
        title: i18n('patientEngagement.details.workflow.workflowHandler.referred', 'engagement'),
        value: referredSR,
      },
      {
        title: i18n('patientEngagement.details.workflow.workflowHandler.cause', 'engagement'),
        value: (serviceRequest?.[0]?.reasonCode ?? [])
          .flatMap((rc: any) =>
            rc.coding?.map((c: any) => c.display) ?? (rc.text ? [rc.text] : [])
          )
          .filter(Boolean)
          .join(', '),
      },
    ],
    [referredSR, serviceRequest]
  );

  const PROVIDER_STEP_CODE = 'PROVIDER_INFORMATION_INTAKE_ITEM';
  const CONSENT_STEP_CODE = 'CONSENT_INFORMATION_INTAKE_ITEM';

  const getIntakeOptionFromChecklistMetadata = (
    checklistItems: any[]
  ): { providers: any[]; consents: any[] } | null => {
    if (!checklistItems?.length) return null;
    let providers: any[] | null = null;
    let consents: any[] | null = null;
    for (const item of checklistItems) {
      if (item?.code !== PROVIDER_STEP_CODE && item?.code !== CONSENT_STEP_CODE) continue;
      try {
        const metadata = item?.metadata ? JSON.parse(item.metadata) : {};
        const intakeOpt = metadata?.intakeOption ?? metadata?.ui?.data?.intakeOption;
        if (intakeOpt?.providers) providers = intakeOpt.providers;
        if (intakeOpt?.consents) consents = intakeOpt.consents;
      } catch {
        // ignore parse errors
      }
    }
    if (providers !== null || consents !== null) {
      return { providers: providers ?? [], consents: consents ?? [] };
    }
    return null;
  };

  const hasProviderOrConsentStep = (checklistItems: any[]): boolean => {
    if (!checklistItems?.length) return false;
    return checklistItems.some(
      (item: any) => item?.code === PROVIDER_STEP_CODE || item?.code === CONSENT_STEP_CODE
    );
  };

  const loadIntakeOptions = async () => {
    if (!workflowInstance?.stage?.id && workflowStage == null) return;
    setIsLoading(true);
    const defaultOptions = { providers: [], consents: [] };
    try {
      const checklistId = workflowInstance?.stage?.id ?? workflowStage;
      const recordId = workflowInstance?.id;
      if (!checklistId || !recordId || !patientEngagement?.patientEngagementId) {
        return;
      }
      const checklistResponse = await WorkflowService.getChecklistById({
        checklistId,
        id: recordId,
        referenceName: patientEngagement?.workflowReferenceName ?? '',
        referenceValue: patientEngagement?.patientEngagementId ?? '',
      });
      const rawItems = checklistResponse?.data ?? checklistResponse;
      const items = Array.isArray(rawItems) ? rawItems : [];
      const fromMetadata = getIntakeOptionFromChecklistMetadata(items);
      if (fromMetadata != null) {
        setIntakeOption(fromMetadata);
        return;
      }
      if (!hasProviderOrConsentStep(items)) {
        return;
      }
      const data = await getRegistries('intake', 'intake-option');
      const dataKeyValue = data?.[0]?.keyValue ? JSON.parse(data[0].keyValue) : null;
      setIntakeOption(dataKeyValue ?? defaultOptions);
    } catch {
      // leave intakeOption as-is; registry useEffect already set it
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!workflowInstance || !patientEngagement?.patientEngagementId) return;
    if (workflowInstance?.stage?.id == null && workflowStage == null) return;
    loadIntakeOptions();
  }, [
    workflowInstance?.id,
    workflowInstance?.stage?.id,
    workflowStage,
    patientEngagement?.patientEngagementId,
  ]);

  useEffect(() => {
    getRegistries('intake', 'intake-option').then((data) => {
      const dataKeyValue = data?.[0]?.keyValue ? JSON.parse(data[0].keyValue) : null;
      if (dataKeyValue) setIntakeOption(dataKeyValue);
    });
  }, []);

  const relatedProviders = useMemo(() => {
    const combinedResources: CareTeamMemberWithEditableData[] = [
      ...(careTeams
        ? careTeams.map((careTeam) => ({
            id: careTeam.id ?? '',
            associatedOrg: 'N/A',
            associatedOrgName: '',
            associatedOrgId: null,
            role: '',
            roleName: 'Care Team',
            roleId: 'care-team',
            name: careTeam.name ?? '',
            identifier: '',
            identifierType: '',
            memberType: careTeam.resourceType,
            startDate: '',
            endDate: '',
            startDateObj: null,
            endDateObj: null,
            versionId: careTeam.meta?.versionId ?? '',
          }))
        : []),
      ...(filteredPractitionerRoles
        ? filteredPractitionerRoles.map((practitionerRole) => ({
            id: practitionerRole.id ?? '',
            associatedOrg: '',
            associatedOrgName: '',
            associatedOrgId: null,
            role: practitionerRole?.code?.[0]?.coding?.[0]?.display ?? '',
            roleName: practitionerRole?.code?.[0]?.coding?.[0]?.display ?? '',
            roleId: practitionerRole?.code?.[0]?.coding?.[0]?.code ?? '',
            name: practitionerRole?.practitioner?.display ?? '',
            identifier: '',
            identifierType: '',
            memberType: practitionerRole.resourceType,
            startDate: '',
            endDate: '',
            startDateObj: null,
            endDateObj: null,
            versionId: practitionerRole.meta?.versionId ?? '',
          }))
        : []),
    ];
    return combinedResources;
  }, [careTeams, filteredPractitionerRoles]);

  useEffect(() => {
    const getConsentsAsync = async () => {
      const consents = await getConsents(patient);
      setConsents(consents);
      setRefreshConsents(false);
    };
    getConsentsAsync();
  }, [patient, refreshConsents]);

  const engagementWorkflowData: WorkflowData = useMemo(
    () => ({
      id: workflowInstance?.id ?? 1,
      recordId: patientEngagement?.patientEngagementId || '',
      recordReference: patientEngagement?.workflowReferenceName,
      stage: {
        id: workflowStage,
      },
      referralRecord: patientEngagement,
      workflowInstance,
    }),
    [workflowStage, workflowInstance, patientEngagement]
  );

  registerSteps({
    PatientTagSelector,
  });

  const WorkflowData: any = {
    disposition,
    reject,
    rejectExternal,
    openDialog: modals.openDialog,
  };

  const createWorkflowHandler = useCallback(
    (modalKey: keyof typeof modals) =>
      ({ checklistItem }: any) => {
        toggleModal(modalKey, true);
        setChecklistData(checklistItem);
      },
    [toggleModal]
  );

  const handlerEngage = createWorkflowHandler('confirmContact');
  const handlerConfirmMemberInfo = createWorkflowHandler('confirmMemberInfo');
  const handlerConfirmationGuarantorInfo = createWorkflowHandler('confirmGuarantorInfo');
  const handlerConfirmationNatOptIn = createWorkflowHandler('confirmNatOptIn');
  const handlerConfirmationScheduleAppointment = createWorkflowHandler('scheduleAppointment');
  const handlerConfirmationReviewNat = createWorkflowHandler('reviewNatReportItem');
  const handlerConfirmationCmOptIn = createWorkflowHandler('confirmOptInItem');
  const handlerConfirmationInitiateNatRequest = createWorkflowHandler('addRequest');
  const handlerConfirmCompleteNatInterview = createWorkflowHandler('showTasksList');

  const providerInfoItemData = useMemo(() => {
    const providers = intakeOption?.providers ?? [];
    const workflowInstanceDataProviders = workflowInstanceDataProvider(
      getParseColumn(workflowInstance, 'data'),
      'notApplicable'
    );

    const data = getSelectOptionCustomButton(relatedProviders ?? [], providers);

    return data?.map((e) => {
      const workflowProvider = workflowInstanceDataProviders?.find(
        (provider) => provider.role === e.role
      );

      return {
        title: e?.sub,
        value: workflowProvider ? workflowProvider.title : e?.title ?? '',
      };
    });
  }, [relatedProviders, intakeOption, workflowInstance]);

  const consentInfoItemData = useMemo(() => {
    const consentRegistry = intakeOption?.consents ?? [];
    const data = getConsentOption(consents, consentRegistry);
    return data?.map((e) => ({
      title: e?.title,
      value: e?.sub,
    }));
  }, [consents, intakeOption]);

  const handlerAssign = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    setChecklistData(checklistItem);

    await assignOwnerHandler({
      record: patientEngagement,
      workflowInstance,
      userEmail,
      referenceValue: patientEngagement?.patientEngagementId,
      referenceName: patientEngagement?.workflowReferenceName,
      checklistItem,
      checklistData,
      more,
      onError: () => {
        enqueueSnackbar('An error has occurred', { variant: 'error' });
        console.log('error on trying to update patient engagement record');
      },
      onFinally: async () => {
        toggleModal('reAssign', false);
        await getPatientEngagementDetails(patientEngagementId);
        await fetchStageFromWorkflow();
      },
    });
  };

  const handleReAssingOwned = async (ownedSelected: any) => {
    try {
      const user: any = allUsers?.find(
        (item: any) => item?.email === ownedSelected?.more?.selectedUser
      );

      const payload = {
        record: {
          ...workflowInstance,
          assigned: {
            selectedUser: user?.id,
          },
        },
      };

      const result = await WorkflowService.reAssignOwner(payload);
      if (result) {
        enqueueSnackbar('User was assigned');
      }
    } catch (err) {
      enqueueSnackbar('An error has occurred', { variant: 'error' });
      console.log('error on handleReAssingOwned');
    } finally {
      await getPatientEngagementDetails(patientEngagementId);
      await fetchStageFromWorkflow();
    }
    toggleModal('reAssign', false);
  };

  const handlerConfirmGeneric = async (data?: any) => {
    const modifiedFields = {
      ...data,
    };

    const payloadForUpdateWorkflowinstance = {
      referenceValue: patientEngagement?.patientEngagementId,
      referenceName: patientEngagement?.workflowReferenceName,
    };

    try {
      await handlerGenericStepWorkflow(
        workflowInstance,
        patientEngagement,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields
      );
    } catch (err) {
      console.log('error', err);
    } finally {
      await fetchStageFromWorkflow();
      await getPatientEngagementDetails(patientEngagementId);
      setChecklistRefreshKey((k) => k + 1);
    }
  };

  const handlerConfirmNATOptIn = async (data: any) => {
    const modifiedFields = {
      opt_out_decision: data.opt_out_decision,
      method: data.method,
    };

    const payloadForUpdateWorkflowinstance = {
      referenceValue: patientEngagement?.patientEngagementId,
      referenceName: patientEngagement?.workflowReferenceName,
    };

    try {
      await handlerGenericStepWorkflow(
        workflowInstance,
        patientEngagement,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields
      );

      let metadata = checklistData?.metadata ? JSON.parse(checklistData.metadata) : {};

      const workflowOutcomeDisposition: string[] | undefined =
        metadata?.ui?.data?.workflowOutcomeDisposition ?? [];

      const isOutcome = workflowOutcomeDisposition?.find(
        (item: string) => item === data.opt_out_decision
      );

      if (workflowOutcomeDisposition && isOutcome) {
        setRejectExternal(true);
      }
    } catch (err) {
      console.log('error', err);
    } finally {
      await getPatientEngagementDetails(patientEngagementId);
      await fetchStageFromWorkflow();
    }
  };

  const handlerConfirmGuarantor = async (data: any) => {
    try {
      let result;
      if (!data?.skip) {
        if (data?.id) {
          result = await updateRelatedPerson(data);
        } else {
          result = await createRelatedPerson(data);
        }
      }

      let modifiedFields: any;

      if (result && !data?.skip) {
        modifiedFields = {
          fhirReference: mapFhirResourceReference(result),
          source: 'workflow',
        };
      }

      const payloadForUpdateWorkflowinstance = {
        referenceValue: patientEngagement?.patientEngagementId,
        referenceName: patientEngagement?.workflowReferenceName,
      };

      await handlerGenericStepWorkflow(
        workflowInstance,
        patientEngagement,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields
      );
    } catch (err) {
      console.log('error', err);
    } finally {
      await getPatientEngagementDetails(patientEngagementId);
      await fetchStageFromWorkflow();
    }
    refreshRelatedPerson();
  };

  const [userPractitionerRole, setUserPractitionerRole] = useState<any>(null);
  useEffect(() => {
    const userPractitionerId = getReferenceId(user?.getCurrentUser()?.user_fhir_uri);
    const orgPieces = user?.getSelectedOrg().fhir_uri.split('/');
    const userOrgFhirId = orgPieces[orgPieces.length - 1];
    const fetchData = async (userPractitionerId, userOrgFhirId) => {
      try {
        const response = await fhirClient.findAll<PractitionerRole>('PractitionerRole', {
          practitioner: userPractitionerId,
          active: true,
          organization: userOrgFhirId,
        });

        if (response?.entry && response?.entry?.length > 0) {
          setUserPractitionerRole(response?.entry[0].resource);
        } else {
          setUserPractitionerRole(null);
        }
      } catch (error) {
        console.error('Error fetching PractitionerRole:', error);
        setUserPractitionerRole(null);
      }
    };

    if (userPractitionerId && userOrgFhirId) {
      fetchData(userPractitionerId, userOrgFhirId);
    }
  }, [user]);

  const taskLinker = useCheckNTaskLinker({ patientId, activityDefinitionId: null });
  const handlerConfirmScheduleAppointment = async (data: any) => {
    try {
      if (!userPractitionerRole) {
        enqueueSnackbar(
          'You must have a practitioner role assigned to schedule appointments. Please contact your administrator.',
          { variant: 'error' }
        );
        return;
      }

      const modifiedFields = {
        date_hour: data.date_hour && new Date(data.date_hour).toISOString(),
        location: data.location,
      };

      let endDateStr: string | undefined = undefined;

      if (data.date_hour) {
        let endDate = new Date(data.date_hour);
        // endDate.setHours(endDate.getHours() + 1);
        endDateStr = endDate.toISOString();
      }

      const payloadForUpdateWorkflowinstance = {
        referenceValue: patientEngagement?.patientEngagementId,
        referenceName: patientEngagement?.workflowReferenceName,
      };

      const metadata = JSON.parse(checklistData.metadata);
      const activityDefinitionRef = metadata?.ui?.data?.activityDefinitionRef;
      const activityDefinitionId = activityDefinitionRef?.split('/')[1];

      if (activityDefinitionId) {
        const practitionerName =
          userPractitionerRole?.practitioner?.display || user.getCurrentUser()?.name;
        let baseTask = {
          executionPeriod: { start: modifiedFields.date_hour, end: endDateStr },
          location: { display: modifiedFields.location },
          owner: userPractitionerRole
            ? {
                display: practitionerName,
                reference: 'PractitionerRole/' + userPractitionerRole.id,
              }
            : null,
        };

        try {
          const assessmentTask = await taskLinker.createAssessmentTask(
            activityDefinitionId,
            baseTask
          );
          const errorData: any = assessmentTask;
          if (errorData?.create === false) {
            enqueueSnackbar(translate(`errors.${errorData?.data?.errorId}`), {
              variant: 'error',
            });
            return;
          }
        } catch (taskError) {
          console.error('Error creating assessment task:', taskError);
          enqueueSnackbar('Failed to create assessment task. Please contact support.', {
            variant: 'error',
          });
          return;
        }
      }

      await handlerGenericStepWorkflow(
        workflowInstance,
        patientEngagement,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields
      );

      enqueueSnackbar('Appointment scheduled successfully!', { variant: 'success' });
    } catch (err) {
      console.error('Error in schedule appointment handler:', err);
      enqueueSnackbar('Failed to schedule appointment. Please try again or contact support.', {
        variant: 'error',
      });
    } finally {
      await getPatientEngagementDetails(patientEngagementId);
      await fetchStageFromWorkflow();
    }
  };

  const handlerConfirmContactAttempt = async (data: any) => {
    let resultContactAttempt: any = {};

    const payloadForUpdateWorkflowinstance = {
      referenceValue: patientEngagement?.patientEngagementId,
      referenceName: patientEngagement?.workflowReferenceName,
    };

    try {
      resultContactAttempt = await PatientEngagementService.createContactAttempt(data);
      if (!_.isEmpty(resultContactAttempt)) {
        const workflowDataScope =
          workflowInstanceDataStep(
            workflowInstance,
            `${checklistData.code}.modifiedFields.contacts`
          ) ?? [];

        workflowDataScope.push({
          outcome: resultContactAttempt?.outcome,
          contactOn: resultContactAttempt?.contactOn,
          nextContactOn: resultContactAttempt?.nextContactOn,
          reasonNote: data?.reasonNote ?? '',
        });

        const modifiedFields = {
          contacts: workflowDataScope,
        };

        await handlerGenericStepWorkflow(
          workflowInstance,
          patientEngagement,
          payloadForUpdateWorkflowinstance,
          checklistData,
          userEmail,
          modifiedFields
        );
        fetchContactAttempt(patientEngagement?.patientEngagementId || '');
        const workflowOutcomeDisposition: string[] | undefined =
          componentsData?.workflow?.initialPatientEngagement?.workflowOutcomeDisposition ?? [];

        const isOutcome = workflowOutcomeDisposition?.find(
          (item: string) => item === data?.outcome
        );

        if (workflowOutcomeDisposition && isOutcome) {
          setRejectExternal(true);
        }
      }
    } catch (err) {
      console.log('error', err);
    } finally {
      await getPatientEngagementDetails(patientEngagementId);
      await fetchStageFromWorkflow();
      setReject(false);
      setRejectExternal(false);
    }
    return resultContactAttempt;
  };

  const handlerInitiateHJRequest = async (data: any) => {
    const modifiedFields = {
      fhirReference: mapFhirResourceReference(data),
    };

    const additionalParams = patient?.id ? { patientId: patient?.id } : null;

    const payloadForUpdateWorkflowinstance = {
      referenceValue: patientEngagement?.patientEngagementId,
      referenceName: patientEngagement?.workflowReferenceName,
    };

    try {
      if (!_.isEmpty(data)) {
        await handlerGenericStepWorkflow(
          workflowInstance,
          patientEngagement,
          payloadForUpdateWorkflowinstance,
          checklistData,
          userEmail,
          modifiedFields,
          additionalParams
        );
      }
    } catch (err) {
      console.log('error', err);
    } finally {
      await getPatientEngagementDetails(patientEngagementId);
      await fetchStageFromWorkflow();
    }
    refreshTask();
  };

  const handlerCompleteHJRequest = async (data: any) => {
    try {
      if (data) {
        const modifiedFields = {
          fhirReference: mapFhirResourceReference(data),
        };

        const payloadForUpdateWorkflowinstance = {
          referenceValue: patientEngagement?.patientEngagementId,
          referenceName: patientEngagement?.workflowReferenceName,
        };

        await handlerGenericStepWorkflow(
          workflowInstance,
          patientEngagement,
          payloadForUpdateWorkflowinstance,
          checklistData,
          userEmail,
          modifiedFields
        );
      }
    } catch (err) {
    } finally {
      await getPatientEngagementDetails(patientEngagementId);
      await fetchStageFromWorkflow();
    }
  };

  const handlerConfirmCMOptIN = async (data: any) => {
    const modifiedFields = {
      opt_out_decision: data.opt_out_decision,
      type: data.type,
      notes: data.notes,
    };

    const payloadForUpdateWorkflowinstance = {
      referenceValue: patientEngagement?.patientEngagementId,
      referenceName: patientEngagement?.workflowReferenceName,
    };

    try {
      await handlerGenericStepWorkflow(
        workflowInstance,
        patientEngagement,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields
      );

      let metadata = checklistData?.metadata ? JSON.parse(checklistData.metadata) : {};

      const workflowOutcomeDisposition: string[] | undefined =
        metadata?.ui?.data?.workflowOutcomeDisposition ?? [];

      const isOutcome = workflowOutcomeDisposition?.find(
        (item: string) => item === data.opt_out_decision
      );

      if (workflowOutcomeDisposition && isOutcome) {
        setRejectExternal(true);
      }
    } catch (err) {
      console.log('error', err);
    } finally {
      await getPatientEngagementDetails(patientEngagementId);
      await fetchStageFromWorkflow();
    }
  };

  const handlerComplementaryInfo = async (store: any) => {
    const payloadForUpdateWorkflowinstance = {
      referenceValue: workflowInstance?.referenceValue,
      referenceName: workflowInstance?.referenceName,
      data: JSON.stringify({
        scope: {
          checklistItem: {
            ...store,
          },
        },
      }),
    };

    try {
      await WorkflowService.updateWorkflowInstance(payloadForUpdateWorkflowinstance);
    } catch (err) {
      console.log('error on handleComplementaryInfo', err);
    } finally {
      await fetchStageFromWorkflow();
    }
  };

  const handlerReferralInfo = createWorkflowHandler('referralInfo');
  const handlerConsents = createWorkflowHandler('consents');
  const handlerProviderInformation = createWorkflowHandler('providerInformation');
  const handlerScreeningInterview = createWorkflowHandler('screeningInterview');
  const handlerChartReview = createWorkflowHandler('chartReview');
  const handlerConfirmScreeningResponses = createWorkflowHandler('confirmScreeningResponses');

  const handlerReferralInformation = async (data: any) => {
    setIsLoading(true);
    try {
      const referral = await ReferralService.createChildReferral(data);

      const modifiedFields = {
        referral: {
          fhirReference: mapFhirResourceReference(referral),
          createdOn: moment().toISOString(),
          createdBy: userEmail,
          source: 'workflow-referral',
        },
      };

      const additionalParams = patient?.id ? { patientId: patient?.id } : null;

      const payloadForUpdateWorkflowinstance = {
        referenceValue: patientEngagement?.patientEngagementId,
        referenceName: patientEngagement?.workflowReferenceName,
      };

      await handlerGenericStepWorkflow(
        workflowInstance,
        patientEngagement,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields,
        additionalParams
      );
    } catch (err) {
      console.log('error', err);
    } finally {
      await getPatientEngagementDetails(patientEngagementId);
      await fetchStageFromWorkflow();
      setIsLoading(false);
    }
    refreshServiceRequest();
  };

  const handlerProvidersInformation = async (data: PayloadProvider) => {
    setIsLoading(true);
    try {
      const { generalPractitioner, addCareTeam, removeCareTeam, workflowDataScope } = data;
      if (generalPractitioner && generalPractitioner.length > 0) {
        await updatePatient(
          produce(patient!, (draft) => {
            draft.generalPractitioner = generalPractitioner.map((e) => ({ reference: e }));
          })
        );
      }

      if (addCareTeam || removeCareTeam) {
        const careTeamsArray = [...addCareTeam, ...removeCareTeam];
        const wrapper = careTeamsArray?.map((e) => ({
          resourceType: e?.resourceType,
          id: e?.id,
          category: e?.category,
          encounter: e?.encounter,
          identifier: e?.identifier,
          managingOrganization: e?.managingOrganization,
          name: e?.name,
          note: e?.note,
          participant: e?.participant,
          period: e?.period,
          reasonCode: e?.reasonCode,
          reasonReference: e?.reasonReference,
          status: e?.status,
          subject: e?.subject,
          telecom: e?.telecom,
        }));
        await updateCareTeams(wrapper);
      }

      const modifiedFields = {
        providers: workflowDataScope,
      };

      const additionalParams = patient ? { patientId: patient?.id } : null;

      const payloadForUpdateWorkflowinstance = {
        referenceValue: patientEngagement?.patientEngagementId,
        referenceName: patientEngagement?.workflowReferenceName,
      };

      await handlerGenericStepWorkflow(
        workflowInstance,
        patientEngagement,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields,
        additionalParams
      );
    } catch (err) {
      console.log('error', err);
    } finally {
      await getPatientEngagementDetails(patientEngagementId);
      await fetchStageFromWorkflow();
      setIsLoading(false);
    }
    refreshCareTeams();
    refreshPractitionerRoles();
    refreshPatient();
  };

  const handlerConsentInformation = async (data: any) => {
    setRefreshConsents(true);

    const modifiedFields = {
      consents: data,
    };

    const additionalParams = patient ? { patientId: patient?.id } : null;

    const payloadForUpdateWorkflowinstance = {
      referenceValue: patientEngagement?.patientEngagementId,
      referenceName: patientEngagement?.workflowReferenceName,
    };

    try {
      await handlerGenericStepWorkflow(
        workflowInstance,
        patientEngagement,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields,
        additionalParams
      );
    } catch (err) {
      console.log('error', err);
      setRefreshConsents(false);
    } finally {
      await getPatientEngagementDetails(patientEngagementId);
      await fetchStageFromWorkflow();
    }
  };

  const handlerConfirmCareTeam = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    setChecklistData(checklistItem);
    const { resourceRelated, status, response: responseMore } = more;
    setIsLoading(true);
    try {
      if (status && resourceRelated) {
        const mappedTask = await mapActivityDefinitionTask(resourceRelated, patient, status);

        if (!mappedTask) {
          enqueueSnackbar(
            `${i18n(
              'patientEngagement.details.workflow.checklist.confirmCareTeam.notExistAD',
              'engagement'
            )}`,
            { variant: 'error' }
          );
          return;
        }

        const response = await createTask(mappedTask);

        const modifiedFields = {
          fhirReference: mapFhirResourceReference(response),
          response: responseMore,
        };

        const payloadForUpdateWorkflowinstance = {
          referenceValue: patientEngagement?.patientEngagementId,
          referenceName: patientEngagement?.workflowReferenceName,
        };

        await handlerGenericStepWorkflow(
          workflowInstance,
          patientEngagement,
          payloadForUpdateWorkflowinstance,
          checklistItem,
          userEmail,
          modifiedFields
        );
      } else {
        enqueueSnackbar(
          `${i18n(
            'patientEngagement.details.workflow.checklist.confirmCareTeam.checklistItemData',
            'engagement'
          )}`,
          { variant: 'error' }
        );
      }
    } catch (err) {
    } finally {
      await getPatientEngagementDetails(patientEngagementId);
      await fetchStageFromWorkflow();
      setIsLoading(false);
    }
  };

  // 1. Define independent, reusable workflow actions based on component types
  const BaseHandlers = {
    assignOwner: {
      type: 'payload',
      handler: handlerAssign,
      data: allUsers,
    },
    contactPatient: {
      type: 'payload',
      handler: handlerEngage,
      data: true,
    },
    confirmMemberInfo: {
      type: 'confirm',
      handler: handlerConfirmMemberInfo,
      data: true,
    },
    confirmGuarantorInfo: {
      type: 'confirm',
      handler: handlerConfirmationGuarantorInfo,
      data: true,
    },
    confirmNatOptIn: {
      type: 'confirm',
      handler: handlerConfirmationNatOptIn,
      data: true,
    },
    scheduleNatSession: {
      type: 'confirm',
      handler: handlerConfirmationScheduleAppointment,
      data: true,
    },
    reviewNatReport: {
      type: 'confirm',
      handler: handlerConfirmationReviewNat,
      data: true,
    },
    confirmCmOptIn: {
      type: 'confirm',
      handler: handlerConfirmationCmOptIn,
      data: true,
    },
    initiateNatRequest: {
      type: 'confirm',
      handler: handlerConfirmationInitiateNatRequest,
      data: true,
    },
    completeNatInterview: {
      type: 'confirm',
      handler: handlerConfirmCompleteNatInterview,
      data: true,
    },
    referralInformation: {
      type: 'payload',
      handler: handlerReferralInfo,
      data: serviceRequest ? referralInfoItemData : true,
    },
    consentInformation: {
      type: 'payload',
      handler: handlerConsents,
      data: consentInfoItemData ? consentInfoItemData : true,
    },
    providerInformation: {
      type: 'payload',
      handler: handlerProviderInformation,
      data: providerInfoItemData ? providerInfoItemData : true,
    },
    screeningInterview: {
      type: 'payload',
      handler: handlerScreeningInterview,
      data: true,
    },
    chartReview: {
      type: 'payload',
      handler: handlerChartReview,
      data: true,
    },
    confirmScreeningResponses: {
      type: 'payload',
      handler: handlerConfirmScreeningResponses,
      data: true,
    },
    confirmCareTeam: {
      type: 'payload',
      handler: handlerConfirmCareTeam,
      data: true,
    },

    // Workflow transition actions
    setDisposition: {
      type: 'confirm',
      handler: () => setDisposition(true),
      data: false,
    },
    setReject: {
      type: 'confirm',
      handler: (open: boolean) => setReject(open),
      data: false,
    },
    setRejectExternal: {
      type: 'confirm',
      handler: (open: boolean) => setRejectExternal(open),
      data: false,
    },
    setOpenDialog: {
      type: 'openDialog',
      handler: (open: boolean = true) => toggleModal('openDialog', open),
      data: false,
    },
  };

  // 2. Construct the WorkflowHandler with the actions and map legacy codes
  const WorkflowHandler: any = {
    ...BaseHandlers,

    // Backward compatibility mappings for legacy hardcoded step codes
    ASSIGN_OWNER_INITIAL_PATIENT_ITEM: BaseHandlers.assignOwner,
    CT_PE_ASSIGN_OWNER_ITEM: BaseHandlers.assignOwner,
    ASSIGN_OWNER_INTAKE_ITEM: BaseHandlers.assignOwner,
    CONTACT_INITIAL_PATIENT_ITEM: BaseHandlers.contactPatient,
    CONTACT_INTAKE_ITEM: BaseHandlers.contactPatient,
    CONFIRM_MEMBER_INFO_INITIAL_PATIENT_ITEM: BaseHandlers.confirmMemberInfo,
    CONFIRM_MEMBER_INFO_INTAKE_ITEM: BaseHandlers.confirmMemberInfo,
    CONFIRM_GUARANTOR_INFO_INITIAL_PATIENT_ITEM: BaseHandlers.confirmGuarantorInfo,
    CONFIRM_GUARANTOR_INFO_INTAKE_ITEM: BaseHandlers.confirmGuarantorInfo,
    CONFIRM_NAT_OPTIN_INITIAL_PATIENT_ITEM: BaseHandlers.confirmNatOptIn,
    SCHEDULE_NAT_SESSION_INITIAL_PATIENT_ITEM: BaseHandlers.scheduleNatSession,
    SCHEDULE_NAT_SESSION_INTAKE_ITEM: BaseHandlers.scheduleNatSession,
    REVIEW_NAT_REPORT_INITIAL_PATIENT_ITEM: BaseHandlers.reviewNatReport,
    CONFIRM_CM_OPTIN_INITIAL_PATIENT_ITEM: BaseHandlers.confirmCmOptIn,
    INITIATE_NAT_REQUEST_INITIAL_PATIENT_ITEM: BaseHandlers.initiateNatRequest,
    COMPLETE_NAT_INTERVIEW_INITIAL_PATIENT_ITEM: BaseHandlers.completeNatInterview,
    REFERRAL_INFORMATION_INTAKE_ITEM: BaseHandlers.referralInformation,
    CONSENT_INFORMATION_INTAKE_ITEM: BaseHandlers.consentInformation,
    PROVIDER_INFORMATION_INTAKE_ITEM: BaseHandlers.providerInformation,
    SCREENING_INTERVIEW_INTAKE_ITEM: BaseHandlers.screeningInterview,
    CHART_REVIEW_INTAKE_ITEM: BaseHandlers.chartReview,
    CONFIRM_SCREENING_RESPONSES_INTAKE_ITEM: BaseHandlers.confirmScreeningResponses,
    CONFIRM_CARE_TEAM_INTAKE_ITEM: BaseHandlers.confirmCareTeam,
  };

  const defaultStructure = useMemo(() => {
    const checklistItem = getParseColumn(workflowInstance, 'data')?.scope?.checklistItem;

    return {
      ...(checklistItem?.ASSIGN_OWNER_INTAKE_ITEM && {
        ASSIGN_OWNER_INTAKE_ITEM: {
          ...(checklistItem?.ASSIGN_OWNER_INTAKE_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.ASSIGN_OWNER_INTAKE_ITEM?.validatedOn
            ),
          }),
        },
      }),
      ...(checklistItem?.CONFIRM_MEMBER_INFO_ITEM && {
        CONFIRM_MEMBER_INFO_ITEM: {
          ...(checklistItem?.CONFIRM_MEMBER_INFO_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.CONFIRM_MEMBER_INFO_ITEM?.validatedOn
            ),
          }),
        },
      }),
      ...(checklistItem?.CONFIRM_NAT_OPTIN_ITEM && {
        CONFIRM_NAT_OPTIN_ITEM: {
          ...(checklistItem?.CONFIRM_NAT_OPTIN_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(checklistItem?.CONFIRM_NAT_OPTIN_ITEM?.validatedOn),
          }),
        },
      }),
      ...(checklistItem?.CONFIRM_GUARANTOR_INFO_ITEM && {
        CONFIRM_GUARANTOR_INFO_ITEM: {
          ...(checklistItem?.CONFIRM_GUARANTOR_INFO_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.CONFIRM_GUARANTOR_INFO_ITEM?.validatedOn
            ),
          }),
        },
      }),
      ...(checklistItem?.SCHEDULE_NAT_SESSION_ITEM && {
        SCHEDULE_NAT_SESSION_ITEM: {
          ...(checklistItem?.SCHEDULE_NAT_SESSION_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.SCHEDULE_NAT_SESSION_ITEM?.validatedOn
            ),
          }),
          modifiedFields: {
            ...(checklistItem?.SCHEDULE_NAT_SESSION_ITEM?.modifiedFields?.date_hour && {
              date_hour: checklistItem?.SCHEDULE_NAT_SESSION_ITEM?.modifiedFields?.date_hour,
            }),
          },
        },
      }),
      ...(checklistItem?.INITIATE_NAT_REQUEST_ITEM && {
        INITIATE_NAT_REQUEST_ITEM: {
          ...(checklistItem?.INITIATE_NAT_REQUEST_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.INITIATE_NAT_REQUEST_ITEM?.validatedOn
            ),
          }),
        },
      }),
      ...(checklistItem?.REVIEW_NAT_REPORT_ITEM && {
        REVIEW_NAT_REPORT_ITEM: {
          ...(checklistItem?.REVIEW_NAT_REPORT_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(checklistItem?.REVIEW_NAT_REPORT_ITEM?.validatedO),
          }),
        },
      }),
      ...(checklistItem?.CONFIRM_CM_OPTIN_ITEM && {
        CONFIRM_CM_OPTIN_ITEM: {
          ...(checklistItem?.CONFIRM_CM_OPTIN_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(checklistItem?.CONFIRM_CM_OPTIN_ITEM?.validatedOn),
          }),
        },
      }),
      ...(checklistItem?.REFERRAL_INFORMATION_INTAKE_ITEM && {
        REFERRAL_INFORMATION_INTAKE_ITEM: {
          ...(checklistItem?.REFERRAL_INFORMATION_INTAKE_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.REFERRAL_INFORMATION_INTAKE_ITEM?.validatedOn
            ),
          }),
        },
      }),
      ...(checklistItem?.CONFIRM_MEMBER_INFO_INTAKE_ITEM && {
        CONFIRM_MEMBER_INFO_INTAKE_ITEM: {
          ...(checklistItem?.CONFIRM_MEMBER_INFO_INTAKE_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.CONFIRM_MEMBER_INFO_INTAKE_ITEM?.validatedOn
            ),
          }),
        },
      }),
      ...(checklistItem?.CONFIRM_GUARANTOR_INFO_INTAKE_ITEM && {
        CONFIRM_GUARANTOR_INFO_INTAKE_ITEM: {
          ...(checklistItem?.CONFIRM_GUARANTOR_INFO_INTAKE_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.CONFIRM_GUARANTOR_INFO_INTAKE_ITEM?.validatedOn
            ),
          }),
        },
      }),
      ...(checklistItem?.CONSENT_INFORMATION_INTAKE_ITEM && {
        CONSENT_INFORMATION_INTAKE_ITEM: {
          ...(checklistItem?.CONSENT_INFORMATION_INTAKE_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.CONSENT_INFORMATION_INTAKE_ITEM?.validatedOn
            ),
          }),
        },
      }),
      ...(checklistItem?.SCHEDULE_NAT_SESSION_INTAKE_ITEM && {
        SCHEDULE_NAT_SESSION_INTAKE_ITEM: {
          ...(checklistItem?.SCHEDULE_NAT_SESSION_INTAKE_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.SCHEDULE_NAT_SESSION_INTAKE_ITEM?.validatedOn
            ),
          }),
        },
      }),
      ...(checklistItem?.PROVIDER_INFORMATION_INTAKE_ITEM && {
        PROVIDER_INFORMATION_INTAKE_ITEM: {
          ...(checklistItem?.PROVIDER_INFORMATION_INTAKE_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.PROVIDER_INFORMATION_INTAKE_ITEM?.validatedOn
            ),
          }),
        },
      }),
      ...(checklistItem?.SCREENING_INTERVIEW_INTAKE_ITEM && {
        SCREENING_INTERVIEW_INTAKE_ITEM: {
          ...(checklistItem?.SCREENING_INTERVIEW_INTAKE_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.SCREENING_INTERVIEW_INTAKE_ITEM?.validatedOn
            ),
          }),
        },
      }),
      ...(checklistItem?.CHART_REVIEW_ITEM && {
        SCREENING_INTERVIEW_ITEM: {
          ...(checklistItem?.SCREENING_INTERVIEW_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.SCREENING_INTERVIEW_ITEM?.validatedOn
            ),
          }),
        },
      }),
      ...(checklistItem?.CONFIRM_SCREENING_RESPONSES_INTAKE_ITEM && {
        CONFIRM_SCREENING_RESPONSES_INTAKE_ITEM: {
          ...(checklistItem?.CONFIRM_SCREENING_RESPONSES_INTAKE_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.CONFIRM_SCREENING_RESPONSES_INTAKE_ITEM?.validatedOn
            ),
          }),
        },
      }),
      ...(checklistItem?.CONTACT_INTAKE_ITEM && {
        CONTACT_INTAKE_ITEM: {
          ...(checklistItem?.CONTACT_INTAKE_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(checklistItem?.CONTACT_INTAKE_ITEM?.validatedOn),
          }),
        },
      }),
      ...(checklistItem?.CONFIRM_CARE_TEAM_INTAKE_ITEM && {
        CONFIRM_CARE_TEAM_INTAKE_ITEM: {
          ...(checklistItem?.CONFIRM_CARE_TEAM_INTAKE_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.CONFIRM_CARE_TEAM_INTAKE_ITEM?.validatedOn
            ),
          }),
        },
      }),
    };
  }, [workflowInstance, configurations]);

  const patientEngagementPermissions: PatientEngagementPermissions = useMemo(
    () => ({
      isAllowedToEdit: checkAclValidation({
        acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.EDIT],
      }),
      notes: {
        isAllowedToAdd: checkAclValidation({
          acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.NOTES.ADD],
        }),
        isAllowedToView: checkAclValidation({
          acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.VIEW],
        }),
      },
    }),
    []
  );

  const isAllLoading =
    isPatientFetching ||
    isCareTeamFetching ||
    isPractitionerRoleFetching ||
    isLoading ||
    refreshConsents;

  const titleFromWorkflowInstance = useMemo(
    () => (workflowInstance?.workFlow?.name ? `${workflowInstance?.workFlow?.name} Details` : ''),
    [workflowInstance]
  );

  const taskActivityDefinition = useTaskActivityDefinition();

  const [tasks, { isLoading: isTasksLoading, refresh: refreshTaskList }] = useTasks({
    filter: {
      subject: patient?.id,
    },
    map: TaskWrapper,
  });

  const taskFilterByActivityDefinition = useMemo(
    () => filterTasksByDefinition(tasks, taskActivityDefinition?.engegement),
    [tasks, taskActivityDefinition]
  );

  const taskPermissions: TaskPermissions = useMemo(
    () => ({
      isAllowedToAdd: checkAclValidation(
        { acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.TASKS.ADD] },
        ACLValidationMode.INCLUSIVE
      ),
      isAllowedToEdit: checkAclValidation(
        { acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.TASKS.EDIT] },
        ACLValidationMode.INCLUSIVE
      ),
      notes: {
        isAllowedToAdd: checkAclValidation(
          { acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.TASKS.NOTES.ADD] },
          ACLValidationMode.INCLUSIVE
        ),
        isAllowedToView: checkAclValidation(
          { acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.TASKS.NOTES.VIEW] },
          ACLValidationMode.INCLUSIVE
        ),
      },
      isAllowedToAssignProgram: checkAclValidation(
        {
          acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.TASKS.HANDLER.ASSIGN_PROGRAM],
        },
        ACLValidationMode.INCLUSIVE
      ),
      isAllowedReferralInternal: checkAclValidation(
        {
          acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.TASKS.HANDLER.REFERRAL_INTERNAL],
        },
        ACLValidationMode.INCLUSIVE
      ),
      isAllowedReferralExternal: checkAclValidation(
        {
          acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.TASKS.HANDLER.REFERRAL_EXTERNAL],
        },
        ACLValidationMode.INCLUSIVE
      ),
      isAllowedAppointmentScheduling: checkAclValidation(
        {
          acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.TASKS.HANDLER.APPOINTMENT],
        },
        ACLValidationMode.INCLUSIVE
      ),
      isAllowedWarmHandoff: checkAclValidation(
        {
          acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.TASKS.HANDLER.WARM_HANDOFF],
        },
        ACLValidationMode.INCLUSIVE
      ),
    }),
    []
  );

  const { healthCareServices, isLoading: isHealthCareServicesLoading } =
    useHealthCareServicesByServiceRequests(null, patient?.id);

  const {
    openAssessmentFrame,
    selectedTaskAssessmentUrl,
    handleAssessmentSelect,
    onCancelAssessment,
  } = useTaskAssessment(tasks, refreshTaskList);

  const refreshEntities = useCallback(() => {
    refreshTaskList();
  }, []);

  useEffect(() => {
    onEvent(CREATE_TASK, refreshEntities);
    onEvent(UPDATE_TASK, refreshEntities);
    return () => {
      removeEvent(CREATE_TASK, refreshEntities);
      removeEvent(UPDATE_TASK, refreshEntities);
    };
  }, []);

  return (
    <Page title={titleFromWorkflowInstance}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs
          title={titleFromWorkflowInstance}
          heading=""
          links={[
            { name: `${i18n('admin.list.dashboard')}`, href: PATH_DASHBOARD.root },
            { name: `${i18n('title', 'engagement')}` },
            {
              name: `${i18n('patientEngagement.title', 'engagement')}`,
              href: PATH_DASHBOARD.engagementHub.patientEngagement,
            },
            { name: titleFromWorkflowInstance },
          ]}
        />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {patient && metadataWorkflow?.workflowHeader && (
              <WorkflowHeader patient={patient} configurations={configurations} i18n={i18n} />
            )}

            {checkAclValidation({
              acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.STATISTICS.VIEW],
            }) && (
              <WorkflowStatistics
                workflowTitle={workflowInstance?.workFlow?.name ?? ''}
                statisticsData={statisticsData}
                path={'patientEngagement.details'}
                namespace="engagement"
                i18n={i18n}
              />
            )}

            <Workflow
              data={engagementWorkflowData}
              refreshRecord={() => fetchStageFromWorkflow()}
              initialWorkflow={patientEngagement?.workflowScope}
              workflowHandler={WorkflowHandler}
              workflowData={WorkflowData}
              i18Path="patientEngagement.details.workflow"
              namespace="engagement"
              checklistRefreshKey={checklistRefreshKey}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <ExpandableCard title={`${i18n('patients.title', 'crs')} Engagement`}>
              <PatientEngagementInformation
                workflowInstance={workflowInstance}
                patientLanguage={patientEngagement?.patientLanguage}
                patientEngagementPermissions={patientEngagementPermissions}
                onOpenAssigned={() => toggleModal('reAssign', true)}
                showOptInStatus={showOptInStatus}
                tasks={tasks}
                consent={consent}
              />
            </ExpandableCard>
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <ExpandableCard title={i18n('patients.title', 'crs')}>
              <Box sx={{ px: 3, pb: 2 }}>
                {patient?.getMRN(configurations?.mpi?.systemMrn)?.value && (
                  <KeyValueGrid
                    label="MRN"
                    value=""
                    children={
                      <CustomLink
                        to={PATH_DASHBOARD.crs.patient.details.forId(patient?.id!)}
                        target="_blank"
                      >
                        <Typography noWrap variant="subtitle2">
                          {patient?.getMRN(configurations?.mpi?.systemMrn)?.value}
                        </Typography>
                      </CustomLink>
                    }
                  />
                )}
                <KeyValueGrid label="Given Name" value={patientEngagement?.patientGiven || ''} />
                <KeyValueGrid label="Family Name" value={patientEngagement?.patientFamily || ''} />
                <KeyValueGrid
                  label="DOB"
                  value={
                    (patientEngagement?.patientDOB &&
                      dateYearFormatWithAge(patientEngagement?.patientDOB)) ||
                    ''
                  }
                />
                {!_.isEmpty(patientEngagement?.patientLastSIL) && (
                  <KeyValueGrid
                    label="SIL"
                    value={
                      patientEngagement?.patientLastSIL
                        ? setSILDisplay(patientEngagement?.patientLastSIL)
                        : ''
                    }
                  />
                )}
              </Box>
            </ExpandableCard>
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <ExpandableCard title="Complementary Info">
              <ComplementaryInfo
                dataSchema={getParseColumn(workflowInstance, 'dataSchema')}
                data={getParseColumn(workflowInstance, 'data')}
                defaultStructure={defaultStructure}
                handleComplementaryInfo={handlerComplementaryInfo}
                isAllowedToEdit={patientEngagementPermissions.isAllowedToEdit}
              />
            </ExpandableCard>
          </Grid>

          <Grid item xs={12}>
            <ExpandableCard title={i18n('patients.details.cases.title', 'crs')}>
              <PatientCase patientId={patientId} disableActions i18n={i18n} />
            </ExpandableCard>
          </Grid>

          {checkAclValidation(
            { acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.TASKS.VIEW] },
            ACLValidationMode.INCLUSIVE
          ) && (
            <Grid item xs={12}>
              <ExpandableCard title={i18n('patients.details.tasks.title', 'crs')}>
                <TasksByPathway
                  permissions={taskPermissions}
                  taskActivityDefinitionContext={taskActivityDefinition?.case}
                  tasks={taskFilterByActivityDefinition}
                  patient={patient as WrappedPatient}
                  healthCareServices={healthCareServices}
                  serviceRequests={serviceRequest}
                  menuTaskHandlers={commonMenuTaskHandlers}
                  onAssessmentSelect={handleAssessmentSelect}
                  allTasksEnabled={true}
                />
              </ExpandableCard>
            </Grid>
          )}

          <Grid item xs={12}>
            <ExpandableCard title="Contact Attempts">
              <TableCustom
                data={Array.isArray(contactAttempts) && mapContactAttempts(contactAttempts)}
                tableHead={TABLE_HEAD_CONTACT}
              />
            </ExpandableCard>
          </Grid>

          {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.GENERAL_PRACTITIONER.VIEW] }) && (
            <Grid item xs={12}>
              <ExpandableCard title={i18n('patients.generalPractitioners.title', 'crs')}>
                <GeneralPractitionerList patient={patient} />
              </ExpandableCard>
            </Grid>
          )}

          {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.RELATED_PERSONS.VIEW] }) && (
            <Grid item xs={12}>
              <ExpandableCard title={i18n('patients.details.relatedPersons.title', 'crs')}>
                <RelatedPersonList
                  patient={patient}
                  open={relatedPersonsOpen}
                  setOpen={setRelatedPersonsOpen}
                />
              </ExpandableCard>
            </Grid>
          )}

          {patientEngagementPermissions?.notes?.isAllowedToView && (
            <Grid item xs={12}>
              <ExpandableCard title="Notes">
                {patient && (
                  <NotesList
                    patient={patient}
                    patientEngagement={patientEngagement}
                    isAllowedToAdd={patientEngagementPermissions?.notes?.isAllowedToAdd}
                  />
                )}
              </ExpandableCard>
            </Grid>
          )}

          {checkAclValidation({
            acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.ATTACHMENTS.VIEW],
          }) && (
            <Grid item xs={12}>
              <ExpandableCard title={i18n('patients.details.attachments.title', 'crs')}>
                <Card style={{ boxShadow: 'none', position: 'static' }}>
                  <DocumentList
                    permissions={{
                      isAllowedToAdd: checkAclValidation({
                        acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.ATTACHMENTS.ADD],
                      }),
                      isAllowedToEdit: checkAclValidation({
                        acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.ATTACHMENTS.EDIT],
                      }),
                    }}
                    patient={patient}
                    documentReferences={documentReferences}
                    refreshDocumentReference={refreshDocumentReference}
                    openCollapseExternal={true}
                    handleUpdateResource={null}
                    showFilters={true}
                  />
                </Card>
              </ExpandableCard>
            </Grid>
          )}

          {checkAclValidation({
            acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.APPOINTMENT.VIEW],
          }) && (
            <Grid item xs={12}>
              <ExpandableCard title={i18n('patients.details.appointments.title', 'crs')}>
                <AppointmentList
                  patient={patient}
                  permissions={{
                    isAllowedAdd: checkAclValidation({
                      acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.APPOINTMENT.ADD],
                    }),
                    isAllowedEdit: checkAclValidation({
                      acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.APPOINTMENT.EDIT],
                    }),
                  }}
                />
              </ExpandableCard>
            </Grid>
          )}
        </Grid>

        <ConfirmMemberInfo
          isOpen={modals.confirmMemberInfo}
          handlerIsOpen={(val) => toggleModal('confirmMemberInfo', val)}
          patient={patient}
          handlePatient={updatePatient}
          handleConfirmMember={handlerConfirmGeneric}
          refreshPatient={refreshPatient}
        />

        <ConfirmGuarantorInfo
          patient={patient}
          isOpen={modals.confirmGuarantorInfo}
          handlerIsOpen={(val) => toggleModal('confirmGuarantorInfo', val)}
          relatedPerson={relatedPersons?.length ? relatedPersons[0] : null}
          handleRelatedPerson={handlerConfirmGuarantor}
        />

        <ConfirmNatOptIn
          isOpen={modals.confirmNatOptIn}
          handlerIsOpen={(val) => toggleModal('confirmNatOptIn', val)}
          handlerConfirmNATOptIn={handlerConfirmNATOptIn}
        />

        <ScheduleAppointment
          isOpen={modals.scheduleAppointment}
          handlerIsOpen={(val) => toggleModal('scheduleAppointment', val)}
          handlerScheduleAppointment={handlerConfirmScheduleAppointment}
        />

        <ConfirmContact
          patient={patient}
          patientEngagementId={patientEngagementId || ''}
          isOpen={modals.confirmContact}
          handlerIsOpen={(val) => toggleModal('confirmContact', val)}
          handlePatient={updatePatient}
          refreshPatient={refreshPatient}
          handleConfirmContactAttempt={handlerConfirmContactAttempt}
          checkListData={checklistData}
        />

        <AddRequest
          open={modals.addRequest}
          patient={patient}
          handleRefresh={() => {
            refreshTask();
            refreshServiceRequest();
          }}
          onCancel={() => toggleModal('addRequest', false)}
          handlerInitiateHJRequest={handlerInitiateHJRequest}
        />

        <TasksList
          patient={patient}
          tasks={natTasks}
          refreshTasks={refreshTask}
          isOpen={modals.showTasksList}
          handlerIsOpen={(val) => toggleModal('showTasksList', val)}
          handleCompleteHJRequest={handlerCompleteHJRequest}
        />

        <ReviewNatReportItem
          patients={patient}
          isOpen={modals.reviewNatReportItem}
          handlerIsOpen={(val) => toggleModal('reviewNatReportItem', val)}
          handleReviewHJRequest={handlerConfirmGeneric}
        />

        <ConfirmCMOptInItem
          isOpen={modals.confirmOptInItem}
          handlerIsOpen={(val) => toggleModal('confirmOptInItem', val)}
          handlerConfirmCMOptIN={handlerConfirmCMOptIN}
        />

        <ReferralInformation
          patient={patient}
          serviceRequest={serviceRequest[0]}
          open={modals.referralInfo}
          isLoading={isAllLoading}
          onClose={() => toggleModal('referralInfo', false)}
          handlerSave={handlerReferralInformation}
          relatedPersonOpen={relatedPersonsOpen}
          setRelatedPersonOpen={setRelatedPersonsOpen}
        />

        <AssessmentFromChecklistData
          patient={patient}
          checkListData={checklistData}
          open={modals.screeningInterview || modals.confirmScreeningResponses || modals.chartReview}
          displayStartRelatedAssessmentFirst={modals.confirmScreeningResponses}
          onClose={() => {
            if (modals.screeningInterview) {
              toggleModal('screeningInterview', false);
            } else if (modals.confirmScreeningResponses) {
              toggleModal('confirmScreeningResponses', false);
            } else if (modals.chartReview) {
              toggleModal('chartReview', false);
            }
          }}
          handlerSave={handlerConfirmGeneric}
        />

        <ProviderInformation
          patient={patient}
          careTeams={careTeams}
          relatedProviders={relatedProviders}
          providerOption={intakeOption?.providers ?? null}
          open={modals.providerInformation}
          isLoading={isAllLoading}
          workflowInstanceData={getParseColumn(workflowInstance, 'data')}
          onClose={() => toggleModal('providerInformation', false)}
          handlerSave={handlerProvidersInformation}
        />

        <ConsentInformation
          patient={patient}
          isLoading={isAllLoading}
          workflowInstanceData={getParseColumn(workflowInstance, 'data')}
          handlerSave={handlerConsentInformation}
          open={modals.consents}
          consentOption={intakeOption?.consents ?? null}
          consents={consents}
          breadcrumbs={[
            `${i18n('patientEngagement.details.title', 'engagement')}`,
            `${i18n('patientEngagement.details.workflow.step.intake', 'engagement')}`,
          ]}
          onClose={() => toggleModal('consents', false)}
        />

        <Dialog
          open={modals.reAssign}
          onClose={() => toggleModal('reAssign', false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              minHeight: '400px',
            },
          }}
        >
          <DialogCloseButton onClose={() => toggleModal('reAssign', false)} />
          <DialogTitle id="alert-dialog-title">Re-Assign To</DialogTitle>
          <DialogContent>
            <SelectType
              others={{
                data: allUsers,
                patient: patientEngagement,
                handler: handleReAssingOwned,
                checklistItem: {},
              }}
            />
          </DialogContent>
        </Dialog>

        {selectedTaskAssessmentUrl && openAssessmentFrame ? (
          <AssessmentFrame
            open={openAssessmentFrame}
            url={selectedTaskAssessmentUrl}
            onCancel={onCancelAssessment}
          />
        ) : null}
      </Container>
    </Page>
  );
}
